import { useEffect, useRef, useState } from "react";
import {
  LiveNarrativeEngine,
  type NarrativeBeat,
  type TelemetrySnapshot,
} from "@/lib/pr36a/liveNarrativeEngine";
import { isProMode, subscribeExperienceMode } from "@/lib/pr36e/experienceModeEngine";
import { useLanguage } from "@/hooks/useLanguage";
import { useDelayedBySignal } from "@/lib/sync/offsetOrchestration";

interface Props {
  minute: number;
  momentum: number;       // -1..1 (positive = home)
  inDanger: boolean;
  attackingHome: boolean; // true if home is attacking
  ballX: number;          // -33..+33
  sprintingCount: number;
  xgPct: number;          // 0..1
  homeLabel: string;
  awayLabel: string;
  starName?: string;
  /** Sampling tick (changes ~1-2Hz). */
  sampleKey: number;
}

/**
 * EXPERIENCE POLISH — Live Narrative Ticker.
 * Premium, lightweight micro-overlay that surfaces short emotional beats
 * from the LiveNarrativeEngine. Throttled to ~15-25s, never blocks gameplay.
 * Hidden in Pro Mode by default (Pro favors data over narrative).
 */
export function LiveNarrativeTicker(props: Props) {
  const engineRef = useRef(new LiveNarrativeEngine());
  const [beat, setBeat] = useState<NarrativeBeat | null>(null);
  const { lang } = useLanguage();
  const [pro, setPro] = useState<boolean>(() => {
    try { return isProMode(); } catch { return false; }
  });

  useEffect(() => subscribeExperienceMode((m) => setPro(m === "pro")), []);

  useEffect(() => {
    if (pro) return; // emotional surface only
    const possessionHome = props.attackingHome ? 0.55 : 0.45;
    const pressureHome = props.attackingHome ? 0.55 + Math.max(0, props.momentum) * 0.3 : 0.4;
    const pressureAway = !props.attackingHome ? 0.55 + Math.max(0, -props.momentum) * 0.3 : 0.4;
    const xgHomeRate = props.attackingHome ? props.xgPct : props.xgPct * 0.2;
    const xgAwayRate = !props.attackingHome ? props.xgPct : props.xgPct * 0.2;
    const fatigue = Math.min(1, props.minute / 110);
    const snap: TelemetrySnapshot = {
      minute: props.minute,
      possessionHome,
      pressureHome,
      pressureAway,
      momentum: props.momentum,
      xgHomeRate,
      xgAwayRate,
      fatigueHome: fatigue,
      fatigueAway: fatigue,
      starName: props.starName,
      starZone: props.ballX > 8 ? "right" : props.ballX < -8 ? "left" : "center",
      homeLabel: props.homeLabel,
      awayLabel: props.awayLabel,
    };
    const next = engineRef.current.tick(snap, Date.now(), lang);
    if (next) {
      setBeat(next);
      const id = window.setTimeout(() => {
        setBeat((cur) => (cur && cur.id === next.id ? null : cur));
      }, 7000);
      return () => window.clearTimeout(id);
    }
  }, [props.sampleKey, pro]); // eslint-disable-line react-hooks/exhaustive-deps

  // Phase 1 — narrative beats are story-flavored, light delay preserves
  // emotional cadence without leaking ahead of live action.
  const delayedBeat = useDelayedBySignal(beat, "narrative_beat");
  if (pro || !delayedBeat) return null;

  const intense = delayedBeat.intensity >= 0.6;
  return (
    <div
      role="status"
      aria-live="polite"
      className="pointer-events-none max-w-full sm:max-w-[240px] animate-slide-up"
    >
      <div
        className={
          "accent-team-border flex items-center gap-1.5 rounded-full border bg-background/70 px-2.5 py-1 text-[10px] font-bold backdrop-blur shadow-md " +
          (intense
            ? "shadow-[var(--team-glow-strong)]"
            : "")
        }
      >
        <span className="text-sm leading-none shrink-0">{delayedBeat.emoji}</span>
        <span className="truncate text-foreground/90">{delayedBeat.text}</span>
      </div>
    </div>
  );
}